import { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../config";

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Unable to read partner logo"));
      }
    });
    reader.addEventListener("error", () => reject(reader.error));
    reader.readAsDataURL(blob);
  });
}

function setPartnerFavicon(dataUrl: string, mimeType: string) {
  let favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!favicon) {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    document.head.appendChild(favicon);
  }

  favicon.href = dataUrl;
  if (mimeType) {
    favicon.type = mimeType;
  }
}

/**
 * Loads the partner logo for the active link as a blob URL so it renders
 * reliably in dev (ngrok) and production. Uses the link-secured widget
 * endpoint (GET /widget/links/:linkId/logo). The endpoint itself is the source
 * of truth for whether a logo exists: a 404 simply means no logo.
 *
 * `settled` flips to true once the request resolves (success or failure) so
 * callers can decide whether to show a splash without blocking indefinitely.
 */
export function usePartnerLogoSrc(linkId: string | undefined) {
  const [src, setSrc] = useState<string | null>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!linkId) {
      // Intentional: reset to the no-logo state when the link clears.
      /* eslint-disable react-hooks/set-state-in-effect */
      setSrc(null);
      setSettled(true);
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    setSettled(false);

    const load = async () => {
      try {
        const apiBase = config.API_BASE_URL.replace(/\/$/, "");
        const headers: Record<string, string> = { "x-link-id": linkId };
        if (apiBase.includes("ngrok")) {
          headers["ngrok-skip-browser-warning"] = "true";
        }

        const response = await axios.get(
          `${apiBase}/widget/links/${linkId}/logo`,
          { responseType: "blob", headers },
        );

        if (cancelled) return;

        const logoBlob = response.data as Blob;
        objectUrl = URL.createObjectURL(logoBlob);
        setSrc(objectUrl);

        try {
          const faviconDataUrl = await blobToDataUrl(logoBlob);
          if (!cancelled) {
            setPartnerFavicon(faviconDataUrl, logoBlob.type);
          }
        } catch {
          // The partner logo can still render even if favicon conversion fails.
        }
      } catch {
        if (!cancelled) {
          setSrc(null);
        }
      } finally {
        if (!cancelled) {
          setSettled(true);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [linkId]);

  return { src, settled };
}
